import {
  useEffect,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
  useState,
} from 'react'
import classNames from 'classnames'
import GetVideoId from 'get-video-id'
import { YouTubePlayer } from 'youtube-player/dist/types'
import { Options, Player } from 'vimeo__player'
import './a-video.css'

type Props = {
  src?: string
  controls?: boolean
  playsInline?: boolean
  className?: string
  orientation?: 'horizontal' | 'vertical'
}

export type AVideoMethods = {
  play: () => void
  pause: () => void
}

const defaultProps = {
  src: '',
}

const AVideo = forwardRef<AVideoMethods, Props>(
  (
    { src, controls, playsInline = false, className, orientation }: Props,
    ref
  ) => {
    const ytPlayer = useRef<YouTubePlayer | null>(null)
    const vimeoPlayer = useRef<Player | null>(null)
    const contentRef = useRef<HTMLDivElement | null>(null)
    const [hasError, setHasError] = useState(false)

    const meta = useMemo(() => {
      return GetVideoId(src ?? '')
    }, [src])

    const destroyPlayers = async () => {
      try {
        await vimeoPlayer.current?.destroy()
      } catch {
        // ignore destroy errors
      }

      try {
        ytPlayer.current?.destroy()
      } catch {
        // ignore destroy errors
      }

      vimeoPlayer.current = null
      ytPlayer.current = null
    }

    const createPlayer = async () => {
      await destroyPlayers()
      setHasError(false)

      if (!contentRef.current || !src || !meta.service) return

      try {
        if (meta.service === 'youtube') {
          const { default: YouTubePlayer } = await import('youtube-player')

          ytPlayer.current = YouTubePlayer(contentRef.current, {
            videoId: meta.id ?? '',
            playerVars: {
              controls: controls ? 1 : 0,
              playsinline: playsInline ? 1 : 0,
              modestbranding: 1,
            },
          })

          return
        }

        if (meta.service === 'vimeo') {
          const { default: VimeoPlayer } = await import('@vimeo/player')

          const playerOptions: Options = {
            controls,
            playsinline: playsInline,
          }

          if (isVimeoPrivateUrl(src)) {
            playerOptions.url = src
          } else {
            const numericId = Number(meta.id)
            if (!Number.isFinite(numericId) || numericId <= 0) {
              throw new Error('Invalid Vimeo video ID')
            }
            playerOptions.id = numericId
          }

          const player = new VimeoPlayer(contentRef.current, playerOptions)
          vimeoPlayer.current = player

          await player.ready()
          return
        }

        setHasError(true)
      } catch (error) {
        setHasError(true)
      }
    }

    useImperativeHandle(
      ref,
      () => ({
        play() {
          if (meta.service === 'youtube' && ytPlayer.current) {
            ytPlayer.current.playVideo()
          } else if (meta.service === 'vimeo' && vimeoPlayer.current) {
            void vimeoPlayer.current.play().catch(() => {})
          }
        },
        pause() {
          if (meta.service === 'youtube' && ytPlayer.current) {
            ytPlayer.current.pauseVideo()
          } else if (meta.service === 'vimeo' && vimeoPlayer.current) {
            void vimeoPlayer.current.pause().catch(() => {})
          }
        },
      }),
      [meta.service]
    )

    useEffect(() => {
      let cancelled = false

      const run = async () => {
        try {
          await createPlayer()
        } catch (error) {
          if (!cancelled) {
            console.error('Video player setup failed', error)
            setHasError(true)
          }
        }
      }

      void run()

      return () => {
        cancelled = true
        void destroyPlayers()
      }
    }, [src, meta.service, meta.id, controls, playsInline])

    return (
      <div
        className={classNames(
          'a-video relative',
          `a-video__${orientation || 'horizontal'}`,
          className
        )}
      >
        {hasError ? (
          <div className="a-video__fallback absolute inset-0 flex items-center justify-center text-center p-4">
            <p>Video unavailable.</p>
          </div>
        ) : (
          <div
            ref={contentRef}
            className="a-video__content absolute inset-0 w-full h-full"
          />
        )}
      </div>
    )
  }
)

const isVimeoPrivateUrl = (src?: string) =>
  !!src && /^https?:\/\/vimeo\.com\/[^/]+\/[^/]+/.test(src)

AVideo.defaultProps = defaultProps

export { AVideo }
